import {
  isTwoFactorEnrollmentRequiredError,
  shouldLogOutOnForbidden,
} from '@/lib/api/twoFactorEnrollment';
import {
  CheckDuplicateSdsesRequestUnit,
  CompanyUpdateRequestBody,
  CompanyUpdateRequestParams,
  CreateCompanySdsRequestUnit,
  DeleteCompanySdsesRequestBody,
  UpdateCompanySdsRequestBody,
  UpdateCompanySdsesRequestBody,
  UpdateReconfirmationSdsRequestBody,
  GetCompanySdsesRequestQuery,
  ExportCompanySdsesRequest,
  RiskAssessmentExportRequest,
  GetCompanySdsesCountRequestQuery,
  DownloadCompanySdsesCSVRequestQuery,
  DownloadRiskAssessmentRecomputeCSVRequestQuery,
  DownloadCompanySdsesPDFRequestQuery,
  UploadCompanySdsesCSVRequestBody,
  CreateAttachmentRequestUnit,
  CreateCompanyPeriodicExportSettingRequest,
  UpdateCompanyPeriodicExportSettingRequest,
  CompanySettingsUpdateRequestBody,
} from '@/interfaces/api/general/company';
import { DynamicListResultRequestBody } from '@/interfaces/api/general/dynamicListResult';
import {
  InquiryRequestBody,
  QuotationRequestBody,
} from '@/interfaces/api/general/info';
import { UserUpdateRequestBody } from '@/interfaces/api/general/user';
import { ApiErrorResponse } from '@/interfaces/api/general/errorResponses';
import { generalApi } from '@/lib/api/general';
import router from '@/router';
import {
  actionCreateCompanyTag,
  actionBulkCreateCompanyTag,
  actionDeleteCompanyTag,
  actionGetCompanyTags,
  actionUpdateCompanyTag,
} from '@/store/main/actions/actionCompanyTag';
import { actionGetCompanySAMLSetting } from '@/store/main/actions/actionCompanySAML';
import { actionGetEditHistories } from '@/store/main/actions/actionSdsEditHistory';
import {
  actionCreateCompanyUser,
  actionDeleteCompanyUser,
  actionGetCompanyUsers,
  actionGetCompanyUserCsv,
  actionPostCompanyUserCsv,
  actionResetCompanyUserPassword,
  actionUpdateCompanyUser,
} from '@/store/main/actions/actionCompanyUser';
import {
  actionCreateCompanyDepartment,
  actionDeleteCompanyDepartment,
  actionGetCompanyDepartmentDeletionImpact,
  actionGetCompanyDepartments,
  actionGetCompanyDepartmentsCsv,
  actionGetCompanyDepartmentTree,
  actionMoveCompanyDepartment,
  actionPostCompanyDepartmentsCsv,
  actionUpdateCompanyDepartment,
} from '@/store/main/actions/actionDepartment';
import {
  actionCreateDynamicList,
  actionGetDynamicList,
  actionUpdateDynamicList,
  actionGetDynamicListResults,
  actionGetDynamicLists,
  actionGetExportDynamicListResults,
  actionRequestDynamicList,
  actionResumeDynamicLists,
  actionSuspendDynamicLists,
  actionDeleteDynamicLists,
  actionFavoriteDynamicLists,
  actionUnfavoriteDynamicLists,
  actionGetDynamicListTemplates,
  actionGetDynamicListTemplate,
  actionGetDynamicListCategories,
  actionCreateDynamicListFromTemplate,
} from '@/store/main/actions/actionDynamicList';
import {
  actionCreateCompanyWhitelistIp,
  actionCreateCompanyWhitelistIpBulk,
  actionDeleteCompanyWhitelistIp,
  actionGetClientIp,
  actionGetCompanyWhitelistIp,
  actionUpdateCompanyWhitelistIpName,
} from '@/store/main/actions/actionWhitelistIpAddress';
import { actionSignUp } from '@/store/main/actions/actionSignUp';
import {
  actionCreateUserSearchCriteria,
  actionDeleteUserSearchCriteria,
  actionGetUserSearchCriteria,
  actionUpdateUserSearchCriteria,
} from '@/store/main/actions/actionUserSearchCriteria';
import { AxiosError, isAxiosError, Method } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { State } from '../state';
import {
  commitAddNotification,
  commitRemoveNotification,
  commitSetCompany,
  commitSetSwitchableCompanies,
  commitSetUserLocale,
  commitSetCurrentUser,
  commitSetDepartments,
  commitSetInitialized,
  commitSetForceEnroll2Fa,
  commitSetFeatureFlags,
} from './mutations';
import { AppNotification, MainState } from './state';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { Locale } from '@/interfaces/api/general/language';
import clerk, {
  getClerkErrorCode,
  getMessageKeyByClerkError,
  isClerkReady,
} from '@/lib/clerk';
import { readCompany, readDepartments, readUserProfile } from './getters';
import {
  actionGetCompanyPasswordSetting,
  actionUpdateCompanyPasswordSetting,
} from './actions/actionPasswordSetting';
import {
  actionGetEnforce2Fa,
  actionUpdateEnforce2Fa,
  actionFetchForceEnroll2Fa,
} from './actions/actionEnforce2Fa';
import {
  actionUpdateFeatureFlags,
  actionStartPollingOfFeatureFlags,
  actionStopPollingOfFeatureFlags,
} from '@/store/main/actions/actionFeatureFlag';

const m = defineMessages({
  documentSent:
    '入力されたメールアドレスに資料を送信しました。メールボックスをご確認ください。',
  inquiryAcknowledgement:
    'お問い合わせいただきありがとうございます。2〜3営業日以内にご返信いたします。',
  estimateAcknowledgement: 'お見積頂きありがとうございます。',
  companyInfoUpdated: 'テナント情報の更新が完了しました',
  companySettingsUpdated_sds_expiration_years:
    '要改訂になる条件の更新が完了しました',
  companySettingsUpdated_sds_revision_copy_fields:
    '同一製品の旧版SDSのコピー項目設定を更新しました',
  userInfoUpdated: 'ユーザー情報を更新しました。',
  tokenExpired: 'トークンの有効期限が切れました再度ログインしてください。',
  resourceNotFound: 'リソースが見つかりませんでした。{details}',
  invalidFileFormat: 'ファイル形式が不正です。{details}',
  requestTimeout: 'タイムアウトしました。{details}',
  payloadTooLarge: 'リクエストが大きすぎます。{details}',
  serverError: '[{status}] サーバーエラーです。{details}',
  unknownError: '不明なエラーです。{details}',
  checkingEmail: '入力されたメールアドレスをチェックしています。',
  passwordRecoveryEmailSent:
    '入力されたメールアドレスにパスワード再発行のメールを送信しました。',
  passwordRecoveryFailed:
    'パスワード再発行の処理に失敗しました。時間をおいて再度お試しください。解消しない場合は運営へご連絡をお願いいたします。',
  passwordResetComplete: 'パスワードのリセット処理が完了しました',
  passwordResetFailed:
    'パスワードリセット処理に失敗しました。有効期限（15分）が切れているか、リンクが無効です。下記リンクより再度リセットをお試しください。\nhttps://sdsocr.com/#/password-recovery',
  passwordReuseError:
    '直近5回の変更で同じパスワードが使用されています。別のパスワードを入力してください。',
  processingError:
    '処理中にエラーが発生しました。運営へご連絡をお願いいたします。',
  uploadFailed:
    'アップロードに失敗しました。アップロードの上限数が契約プラン上限数を超えていないかご確認ください。',
  fileDeleted: '選択したファイルを削除しました。',
  updateSuccess: '更新しました',
  redataRequested: '再データ化依頼を行いました',
  checklistUpdated: 'チェックリストを更新しました',
  checklistAdded: 'チェックリストに追加しました',
  processFailed:
    '処理に失敗しました。ページを更新して、再度実行をお願いいたします。',
  productCodeAlreadyExists: '製品コードに重複があります。',
  checklistRemoved: 'チェックリストから削除しました',
  notificationActivating: 'アカウント登録完了しています。',
  accountRegistrationComplete:
    'アカウント登録完了が完了しました。ログインをお願いいたします。',
  accountActivationMailSent: 'アカウント登録完了メールを送信しています。',
  accountActivationReceived:
    'アカウント登録完了メールの送信が完了しました。24時間以内にアカウント登録完了をお願いいたします。',
  pleaseCheck: '以下の{detail}\n{issues}',
  errorReceivingData: 'データ取得エラーです。{details}',
  errorUpdatingData: '入力内容に誤りがあります。{details}',
  accountActivationExpired:
    '新しいアカウント有効化メールのリンクを再発行する必要があります。',
  saving: '保存中',
  resettingPassword: 'パスワードをリセットしています',
  pdfDownloadSuccess: '選択されたSDSのダウンロードのリクエストを受け付けました',
});

export type MainContext = ActionContext<MainState, State>;

type ApiErrorDetail = string | { key?: string; message?: string };
const extractApiErrorDetailMessage = (detail?: ApiErrorDetail): string =>
  typeof detail === 'string' ? detail : detail?.message ?? '';

const showErrorToast = (
  context: MainContext,
  messageKey: string,
  messageValues: Record<string, string> = {}
) => {
  commitAddNotification(context, { messageKey, messageValues, color: 'error' });
};

const messageKeyPerStatus = (status?: number, method?: Method) => {
  switch (status) {
    case 400:
      return method === 'get' ? m.errorReceivingData : m.errorUpdatingData;
    case 401:
      return m.tokenExpired;
    case 402:
      return m.errorUpdatingData;
    case 403:
      return sharedMessages.noPermission;
    case 404:
      return m.resourceNotFound;
    case 408:
      return m.requestTimeout;
    case 413:
      return m.payloadTooLarge;
    case 415:
      return m.invalidFileFormat;
    case 422:
      return m.errorUpdatingData;
    case 500:
    case 501:
    case 502:
    case 503:
    case 504:
      return m.serverError;
    default:
      return m.unknownError;
  }
};

export const actionsMain = {
  // Init logic that will check whether the user is logged in or not and
  // set the initial state of the application.
  async actionInit(context: MainContext) {
    await isClerkReady;
    if (clerk.session && clerk.session.status === 'active') {
      let user = readUserProfile(context);

      // Self-heal profiles persisted before `scoped_department_ids` existed
      // on /me, so already-logged-in non-admin users aren't left with a
      // permanently empty SDS department picker until they log out/in.
      if (user && user.scoped_department_ids === undefined) {
        user = null;
      }

      // Fetch the user if it wasn't in the persisted state.
      if (!user) {
        try {
          await dispatchGetCurrentUser(context);
          user = readUserProfile(context);
        } catch (error) {
          const axiosError = error as AxiosError<ApiErrorResponse>;
          await dispatchCheckApiError(context, axiosError);
          if (axiosError?.response?.status === 400) {
            if (
              // TODO: We should really work with error codes instead...
              axiosError?.response?.data?.detail === 'Inactive user.' ||
              axiosError?.response?.data?.detail === '非アクティブユーザー'
            ) {
              // typesafe-vuex has an issue with optional parameters
              // so we need to ignore the type error here.
              // @ts-ignore
              await dispatchLogOut(context, { inactiveUser: true });
            }
          } else if (axiosError?.response?.status === 404) {
            // User not found in database (SAML user not registered)
            // Save error message to sessionStorage to display after logout
            sessionStorage.setItem(
              'samlUserNotRegistered',
              JSON.stringify({
                message:
                  'このメールアドレスは登録されていません。管理者にユーザー登録を依頼してください。',
                color: 'error',
              })
            );

            // @ts-ignore
            await dispatchLogOut(context);
          }
          throw axiosError;
        }
      }

      // Fetch departments if they weren't in the persisted state.
      // Check if user has switched to a different company via localStorage
      const selectedCompanyId = localStorage.getItem('selectedCompanyId');
      const targetCompanyId = selectedCompanyId || (user?.company_id ?? '');

      const departments = readDepartments(context);
      const toFetch: Array<Promise<void>> = [];
      if (user) {
        // Always re-fetch the selected/target company on init, even if it was
        // already in the persisted state: active_features (and other company
        // fields) can change server-side while the tenant stays the same, and
        // the persisted store would otherwise never pick that up until logout
        // or a tenant switch.
        toFetch.push(
          dispatchGetCompany(context, { companyId: targetCompanyId }).catch(
            (error) => {
              const axiosError = error as AxiosError<ApiErrorResponse>;
              console.error('Failed to fetch selected company:', error);
              // Only fall back to the user's default company when the switched
              // tenant is no longer accessible. Transient errors (network,
              // 5xx, etc.) should leave the persisted company/tenant selection
              // untouched instead of silently kicking the user back to their
              // home tenant.
              const isAccessError = [401, 403, 404].includes(
                axiosError?.response?.status ?? 0
              );
              if (!isAccessError) {
                return;
              }
              localStorage.removeItem('selectedCompanyId');
              if (targetCompanyId !== user.company_id) {
                // Try to fetch user's default company as fallback
                return dispatchGetCompany(context, {
                  companyId: user.company_id,
                }).catch((fallbackError) => {
                  console.error(
                    'Failed to fetch default company:',
                    fallbackError
                  );
                  // If even default company fails, user will be logged out by actionGetCompany
                });
              }
            }
          )
        );
        if (!departments || selectedCompanyId) {
          // Refetch departments if company was switched
          toFetch.push(
            dispatchGetCompanyDepartments(context, {
              companyId: targetCompanyId,
            }).catch((error) => {
              // Don't fail initialization if departments fetch fails
              console.error('Failed to fetch departments:', error);
            })
          );
        }
        // Fetch switchable companies for the user
        toFetch.push(
          dispatchGetSwitchableCompanies(context)
            .catch((error) => {
              // Don't fail the initialization if switchable companies fetch fails
              console.error('Failed to fetch switchable companies:', error);
            })
            .then(() => undefined)
        );
      }
      await Promise.all(toFetch);

      // Fetch after the company (and its fallback) is resolved, so that the
      // enrollment status is evaluated against the final tenant.
      if (user) {
        await dispatchFetchForceEnroll2Fa(context);
      }

      // Fetch feature flags AFTER company context is determined
      // so that Statsig evaluates flags based on the switched tenant
      await actionUpdateFeatureFlags(context, {
        userId: user?.uuid || null,
        companyId: targetCompanyId,
      });
      actionStartPollingOfFeatureFlags(
        context,
        user?.uuid || null,
        targetCompanyId
      );
    } else {
      // When logged out, check if there is persisted user data and remove it, just in case.
      const user = readUserProfile(context);
      const company = readCompany(context);
      const departments = readDepartments(context);
      if (user) {
        commitSetCurrentUser(context, null);
      }
      commitSetForceEnroll2Fa(context, false);
      if (company) {
        commitSetCompany(context, null);
      }
      if (departments) {
        commitSetDepartments(context, null);
      }
    }
    commitSetInitialized(context, true);
  },
  async actionPostInquiry(
    context: MainContext,
    payload: {
      payload: InquiryRequestBody;
    }
  ) {
    try {
      const response = await generalApi.inquiry(payload.payload);
      if (response.data) {
        commitAddNotification(context, {
          messageKey: m.documentSent,
          color: 'success',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      throw axiosError;
    }
  },
  async actionPostInquiryEn(
    context: MainContext,
    payload: {
      payload: InquiryRequestBody;
    }
  ) {
    try {
      const response = await generalApi.inquiryEn(payload.payload);
      if (response.data) {
        commitAddNotification(context, {
          messageKey: m.inquiryAcknowledgement,
          color: 'success',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      throw axiosError;
    }
  },

  async actionPostQuoation(
    context: MainContext,
    payload: {
      payload: QuotationRequestBody;
    }
  ) {
    try {
      const response = await generalApi.quotation(payload.payload);
      if (response.data) {
        commitAddNotification(context, {
          messageKey: m.estimateAcknowledgement,
          color: 'success',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      throw axiosError;
    }
  },
  actionSignUp,
  async actionLogIn(
    context: MainContext,
    payload: { username: string; password: string }
  ) {
    if (clerk.session && clerk.session.status === 'active') {
      // A login request being made while the user is logged in
      // typically hints to a logic issue, as the login form should
      // not be available when the user is already logged in.
      // So, we will just ignore the request.
      return;
    }
    if (clerk.loaded && clerk.client) {
      try {
        // Password policy pre-check (throws on 403/404, handled below).
        await generalApi.validatePassword(payload.username, payload.password);

        // Whether this user opted into email 2FA (server-side truth from Clerk
        // unsafe_metadata, via a dedicated endpoint). Best-effort: a lookup
        // failure must not block login.
        let emailAuthEnabled = false;
        try {
          const status = await generalApi.getEmail2faStatus(payload.username);
          emailAuthEnabled = !!status.data?.email_2fa_enabled;
        } catch (error) {
          console.error('Failed to fetch email 2FA status:', error);
        }

        // Client Trust's "trusted device" lives on the Clerk client
        // (__client cookie) independently of session lifetime, so it
        // survives sessions that expire without the logout flow (closed
        // tab/browser). For users who enabled email 2FA we destroy the
        // client up front so this sign-in is challenged by email every
        // time. Other users keep the trusted client and are only
        // challenged on genuinely new devices. Best-effort: on failure we
        // proceed with the (possibly trusted) client rather than block
        // login.
        if (emailAuthEnabled) {
          try {
            await clerk.client.destroy();
          } catch (error) {
            console.error(
              'Failed to destroy Clerk client before sign-in:',
              error
            );
          }
        }

        const signIn = await clerk.client.signIn.create({
          identifier: payload.username,
          password: payload.password,
        });

        if (signIn.status === 'complete') {
          await clerk.setActive({ session: signIn.createdSessionId });
          if (!clerk.session) {
            throw new Error('Clerk session not found');
          }

          // Refresh the page to reinitialize cleanly.
          window.location.reload();
        } else if (signIn.status === 'needs_second_factor') {
          const tfaStrategy = signIn.supportedSecondFactors?.filter(
            (f) => f.strategy !== 'backup_code'
          )[0]?.strategy;
          if (!tfaStrategy) {
            throw new Error('Clerk 2FA strategy not found');
          }

          const phoneNumberFactor = signIn?.supportedSecondFactors?.find(
            (sf) => sf.strategy === 'phone_code'
          );
          const phoneNumberId = phoneNumberFactor?.phoneNumberId;

          if (phoneNumberFactor && !phoneNumberId) {
            throw new Error('Clerk phone number not found');
          }

          // Instead of launching the 2FA preparation on the view level
          // we will do it here to avoid SMS spamming via page refreshs.
          if (tfaStrategy === 'phone_code' && phoneNumberId) {
            await signIn.prepareSecondFactor({
              strategy: 'phone_code',
              phoneNumberId,
            });
          }

          // Clerk Client Trust: the second factor is an email code sent to the
          // user's address when signing in from a new/untrusted device. Send it
          // here (like phone_code) to avoid re-emailing on page refreshes.
          // The strategy is matched/cast as a string because the resolved
          // `@clerk/types` union may omit `email_code` even though clerk-js 5.127
          // returns it at runtime.
          const emailCodeFactor = signIn?.supportedSecondFactors?.find(
            (sf) => (sf.strategy as string) === 'email_code'
          ) as unknown as { emailAddressId: string } | undefined;
          if ((tfaStrategy as string) === 'email_code' && emailCodeFactor) {
            await signIn.prepareSecondFactor({
              strategy: 'email_code',
              emailAddressId: emailCodeFactor.emailAddressId,
            } as unknown as Parameters<typeof signIn.prepareSecondFactor>[0]);
          }

          // Forward the current query — notably `redirect` — to the 2FA
          // screen. That value lives only in the URL: verification ends with a
          // `window.location.reload()`, so `ensureLoggedOut` resolves the
          // post-login destination from whatever URL this push produced.
          // Dropping it here stranded users on the dashboard instead of
          // returning them to the regulation satellite app they came from.
          await router.push({
            name: 'loginVerify',
            params: { mode: tfaStrategy },
            query: { ...router.currentRoute.query },
          });
          return;
        } else {
          throw new Error('Clerk login failed');
        }
      } catch (error) {
        // 失敗ログインを監査に記録（fire-and-forget・best-effort）。
        // V2(frontend_next login/page.tsx) の password サインイン catch と同じく、
        // Clerk のエラーコードをそのまま送る。backend が email→tenant を解決し、
        // _FAILURE_CODES に含まれるコード(form_password_incorrect 等)だけ記録する。
        // 応答は無視し、ここで失敗してもログインフローには影響させない。
        void generalApi.reportLoginFailed(
          payload.username,
          getClerkErrorCode(error as Error)
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isApiErrorResponse = (data: any): data is ApiErrorResponse => {
          return data instanceof Object && 'detail' in data;
        };

        const isAxiosErrorWithApiErrorResponse = (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          err: any
        ): err is AxiosError<ApiErrorResponse> => {
          return (
            isAxiosError(err) &&
            !!err.response &&
            isApiErrorResponse(err.response.data)
          );
        };

        if (isAxiosErrorWithApiErrorResponse(error)) {
          return {
            error: true,
            messageText: error.response?.data.detail ?? m.processingError,
          };
        }

        return {
          error: true,
          messageText: getMessageKeyByClerkError(error as Error),
        };
      }
    }
  },
  async actionLogOut(
    context: MainContext,
    payload?:
      | {
          tfaCompleted?: boolean;
          inactiveUser?: boolean;
          logout?: boolean;
        }
      | undefined
  ) {
    // Clear Statsig user identification
    commitSetFeatureFlags(context, {});
    actionStopPollingOfFeatureFlags(context);

    if (clerk.session && clerk.session.status === 'active') {
      // Read the per-user "email 2FA" preference BEFORE signOut (which clears
      // clerk.user). When the user opted into email 2FA, we destroy the Clerk
      // client on logout so the next login is treated as a new/untrusted device
      // and Client Trust re-challenges by email (i.e. email on every login).
      // When off, we leave the client trusted → Client Trust only challenges on
      // a genuinely new device. Client Trust itself is global and can't be gated
      // per user, so this flag only controls our own client-destroy.
      const emailAuthEnabled = !!clerk.user?.unsafeMetadata?.email2faEnabled;

      // server side logout notification
      await generalApi.logoutSAMLProxy();
      await clerk.signOut();

      if (emailAuthEnabled) {
        // Best-effort: never block logout if this fails.
        try {
          await clerk.client?.destroy();
        } catch (error) {
          console.error('Failed to destroy Clerk client on logout:', error);
        }
      }

      // Clear persisted state, but keep the local state until the page is refreshed.
      localStorage.removeItem('vuex');
      localStorage.removeItem('selectedCompanyId');

      // Refresh the page to reinitialize cleanly.
      // Set login state to show the success message on the login page.
      sessionStorage.setItem(
        'loginState',
        JSON.stringify(payload || { logout: true })
      );
      window.location.reload();
    }
  },
  async actionGetCompany(context: MainContext, payload: { companyId: string }) {
    try {
      const response = await generalApi.getCompany(payload.companyId);
      if (response.data) {
        commitSetCompany(context, {
          uploaded_file_number: context.state.company
            ? context.state.company.uploaded_file_number
            : 0,
          name: response.data.name,
          uuid: response.data.uuid,
          phone: response.data.phone,
          country_code: response.data.country_code,
          maximum_upload: response.data.maximum_upload,
          attachment_limit_id: response.data.attachment_limit_id,
          customer_id: response.data.customer_id,
          subscription_id: response.data.subscription_id,
          next_paid_at: response.data.next_paid_at,
          subscribed_at: response.data.subscribed_at,
          renewed_at: response.data.renewed_at,
          active_features: response.data.active_features,
          scim_enabled: response.data.scim_enabled,
          idp_initiated_enabled: response.data.idp_initiated_enabled,
          sds_expiration_years: response.data.sds_expiration_years,
          sds_revision_copy_fields: response.data.sds_revision_copy_fields,
          group_names: response.data.group_names ?? undefined,
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionUpdateCompany(
    context: MainContext,
    payload: {
      params: CompanyUpdateRequestParams;
      payload: CompanyUpdateRequestBody;
    }
  ) {
    try {
      const response = await generalApi.updateCompany(
        payload.params,
        payload.payload
      );
      if (response.data) {
        commitAddNotification(context, {
          messageKey: m.companyInfoUpdated,
          color: 'success',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionUpdateCompanySettings(
    context: MainContext,
    payload: {
      params: CompanyUpdateRequestParams;
      payload: CompanySettingsUpdateRequestBody;
    }
  ) {
    try {
      const response = await generalApi.updateCompanySettings(
        payload.params,
        payload.payload
      );
      if (response.data) {
        const messages: Record<keyof typeof payload.payload, string> = {
          sds_revision_copy_fields:
            m.companySettingsUpdated_sds_revision_copy_fields,
          sds_expiration_years: m.companySettingsUpdated_sds_expiration_years,
        };
        (Object.keys(payload.payload) as Array<keyof typeof messages>).forEach(
          (key) => {
            commitAddNotification(context, {
              messageKey: messages[key],
              color: 'success',
            });
          }
        );
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionDeleteCompany(
    context: MainContext,
    payload: { companyId: string }
  ) {
    try {
      const response = await generalApi.deleteCompany(payload.companyId);
      if (response.data) {
        await dispatchLogOut(context);
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  actionGetCompanyDepartments,
  actionGetCompanyDepartmentTree,
  actionGetCompanyDepartmentDeletionImpact,
  actionMoveCompanyDepartment,
  actionCreateCompanyDepartment,
  actionUpdateCompanyDepartment,
  actionDeleteCompanyDepartment,
  actionGetCompanyDepartmentsCsv,
  actionPostCompanyDepartmentsCsv,

  actionGetCompanyUsers,
  actionCreateCompanyUser,
  actionUpdateCompanyUser,
  actionResetCompanyUserPassword,
  actionDeleteCompanyUser,
  actionGetCompanyUserCsv,
  actionPostCompanyUserCsv,

  actionGetUserSearchCriteria,
  actionCreateUserSearchCriteria,
  actionUpdateUserSearchCriteria,
  actionDeleteUserSearchCriteria,

  actionCreateCompanyWhitelistIp,
  actionCreateCompanyWhitelistIpBulk,
  actionDeleteCompanyWhitelistIp,
  actionGetClientIp,
  actionGetCompanyWhitelistIp,
  actionUpdateCompanyWhitelistIpName,

  async actionGetCurrentUser(context: MainContext) {
    try {
      const response = await generalApi.getMe();
      if (response.data) {
        commitSetCurrentUser(context, {
          uuid: response.data.uuid,
          first_name: response.data.first_name,
          last_name: response.data.last_name,
          middle_name: response.data.middle_name,
          email: response.data.email,
          phone: response.data.phone,
          departments: response.data.departments,
          scoped_department_ids: response.data.scoped_department_ids,
          position: response.data.position,
          company_id: response.data.company_id,
          is_active: response.data.is_active,
          is_subscribe: response.data.is_subscribe,
          is_checklist_notify_subscribe:
            response.data.is_checklist_notify_subscribe,
          role_id: response.data.role_id,
          role: response.data.role,
          created_at: response.data.created_at,
          tfa_phone_number: response.data.tfa_phone_number,
          locale: response.data.locale,
          login_at: response.data.login_at,
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionUpdateCurrentUser(
    context: MainContext,
    payload: UserUpdateRequestBody
  ) {
    try {
      const loadingNotification = {
        messageKey: m.saving,
        showProgress: true,
      };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          generalApi.updateMe(payload),
          new Promise((resolve) => setTimeout(resolve, 500)),
        ])
      )[0];
      commitSetCurrentUser(context, {
        uuid: response.data.uuid,
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        middle_name: response.data.middle_name,
        email: response.data.email,
        phone: response.data.phone,
        departments: response.data.departments,
        scoped_department_ids: response.data.scoped_department_ids,
        position: response.data.position,
        company_id: response.data.company_id,
        is_active: response.data.is_active,
        is_subscribe: response.data.is_subscribe,
        is_checklist_notify_subscribe:
          response.data.is_checklist_notify_subscribe,
        role_id: response.data.role_id,
        role: response.data.role,
        created_at: response.data.created_at,
        tfa_phone_number: response.data.tfa_phone_number,
        locale: response.data.locale,
        login_at: response.data.login_at,
      });
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        messageKey: m.userInfoUpdated,
        color: 'success',
      });
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionCheckApiError(
    context: MainContext,
    payload: AxiosError<ApiErrorResponse>
  ) {
    if (payload.name === 'CanceledError') return;

    const errorStatus = payload.response?.status || payload.status;
    const method = payload.config?.method as Method;
    const errorDetailData = payload.response?.data;
    const detailMessage = extractApiErrorDetailMessage(
      errorDetailData &&
        typeof errorDetailData === 'object' &&
        'detail' in errorDetailData
        ? errorDetailData.detail
        : (errorDetailData as ApiErrorDetail | undefined)
    );
    switch (errorStatus) {
      case 401: {
        showErrorToast(context, messageKeyPerStatus(errorStatus, method));
        await dispatchLogOut(context);
        break;
      }

      case 403: {
        // 2FA必須化のテナントで2FA未設定の場合は、ログアウトせずマイページ（2FAの設定）へ誘導する
        if (isTwoFactorEnrollmentRequiredError(payload)) {
          commitSetForceEnroll2Fa(context, true);
          if (
            router.currentRoute.name !== 'user' &&
            router.currentRoute.name !== 'twoFactorSetup'
          ) {
            router.push({ name: 'user' }).catch(() => undefined);
          }
          break;
        }
        if (
          errorDetailData &&
          typeof errorDetailData === 'object' &&
          !('detail' in errorDetailData) &&
          (errorDetailData as unknown as Record<string, string>).key ===
            '403_auth_ip' &&
          typeof (errorDetailData as unknown as Record<string, string>)
            .message === 'string'
        ) {
          if (typeof window !== 'undefined') {
            sessionStorage.setItem(
              'auth:ip-not-allowed',
              JSON.stringify({
                message: (errorDetailData as unknown as Record<string, string>)
                  .message,
                status: 'error',
              })
            );
          }
          break;
        }
        showErrorToast(context, messageKeyPerStatus(errorStatus, method), {
          details: detailMessage,
        });
        break;
      }

      case 400:
      case 402:
      case 404:
      case 408:
      case 413:
      case 415:
      case 422: {
        showErrorToast(context, messageKeyPerStatus(errorStatus, method), {
          details: detailMessage,
        });
        break;
      }

      case 500:
      case 501:
      case 502:
      case 503:
      case 504: {
        showErrorToast(context, messageKeyPerStatus(errorStatus, method), {
          status: String(errorStatus),
          details: detailMessage,
        });
        break;
      }

      default: {
        showErrorToast(context, messageKeyPerStatus(errorStatus, method), {
          details: detailMessage,
        });
        break;
      }
    }
  },
  actionRouteLoggedIn() {
    if (
      router.currentRoute.path?.startsWith('/login') ||
      router.currentRoute.path === '/signup'
    ) {
      const redirect = router.currentRoute.query.redirect;
      if (typeof redirect === 'string' && redirect.startsWith('/')) {
        router.push({ path: redirect });
      } else {
        router.push({ name: 'sdsUpload' });
      }
    }
  },
  async removeNotification(
    context: MainContext,
    payload: { notification: AppNotification; timeout: number }
  ) {
    return new Promise((resolve) => {
      setTimeout(() => {
        commitRemoveNotification(context, payload.notification);
        resolve(true);
      }, payload.timeout);
    });
  },
  async passwordRecovery(context: MainContext, payload: { username: string }) {
    const loadingNotification = {
      messageKey: m.checkingEmail,
      showProgress: true,
    };

    try {
      commitAddNotification(context, loadingNotification);
      await generalApi.passwordRecovery(payload.username);
      commitRemoveNotification(context, loadingNotification);

      commitAddNotification(context, {
        messageKey: m.passwordRecoveryEmailSent,
        color: 'success',
      });

      router.push({
        name: 'login',
      });
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      commitRemoveNotification(context, loadingNotification);

      const status = axiosError.response?.status;
      const detail = extractApiErrorDetailMessage(
        axiosError.response?.data?.detail
      );

      if (status === 404) {
        // 該当するメールアドレスが存在しない場合のみ、入力内容を疑う文言を出す
        showErrorToast(context, sharedMessages.invalidEmail);
      } else if (detail) {
        // SAML JIT 有効テナントなど、サーバーが理由を返しているケース
        showErrorToast(context, m.errorUpdatingData, { details: detail });
      } else {
        // サーバーエラー・通信エラー。入力内容の問題ではないことを伝える
        showErrorToast(context, m.passwordRecoveryFailed);
      }
      throw axiosError;
    }
  },
  async resetPassword(
    context: MainContext,
    payload: { password: string; passwordResetToken: string }
  ) {
    const loadingNotification = {
      messageKey: m.resettingPassword,
      showProgress: true,
    };
    try {
      commitAddNotification(context, loadingNotification);
      await Promise.all([
        generalApi.resetPassword(payload.passwordResetToken, payload.password),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        messageKey: m.passwordResetComplete,
        color: 'success',
      });
      router.push({
        name: 'login',
      });
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);

      const axiosError = error as AxiosError<ApiErrorResponse | string>;

      if (axiosError?.response?.status === 400) {
        const errorData = axiosError.response.data;

        // Handle both string response and ApiErrorResponse object
        let errorMessage = '';
        let errorCode = '';
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (
          errorData &&
          typeof errorData === 'object' &&
          'detail' in errorData
        ) {
          errorCode = errorData.code || '';
          errorMessage = errorData.detail;
        }

        switch (errorCode) {
          case 'PASSWORD_TOO_SHORT':
            commitAddNotification(context, {
              color: 'error',
              // getting password long setting should be available in the backend state
              messageKey: errorMessage,
            });
            return;
          case 'PASSWORD_REUSE_ERROR':
            commitAddNotification(context, {
              color: 'error',
              messageKey: m.passwordReuseError,
            });
            return;
        }

        const passwordReusePatterns = [
          '直近5回の変更で同じパスワードが使用されています。別のパスワードを入力してください。',
          'This password has been used in the last five changes.',
        ];
        if (
          errorMessage &&
          passwordReusePatterns.some((pattern) =>
            errorMessage.includes(pattern)
          )
        ) {
          commitAddNotification(context, {
            color: 'error',
            messageKey: m.passwordReuseError,
          });
          return;
        }
      }
      commitAddNotification(context, {
        color: 'error',
        messageKey: m.passwordResetFailed,
      });
    }
  },
  async actionActivateUser(context: MainContext, payload: { token: string }) {
    const notificationActivating = {
      messageKey: m.notificationActivating,
      showProgress: true,
    };
    const notificationActivated = {
      messageKey: m.accountRegistrationComplete,
      showProgress: false,
    };
    try {
      commitAddNotification(context, notificationActivating);
      await generalApi.activateUser(payload);
      commitRemoveNotification(context, notificationActivating);
      commitAddNotification(context, notificationActivated);
    } catch (error) {
      commitRemoveNotification(context, notificationActivating);
      commitAddNotification(context, {
        color: 'error',
        messageKey: m.accountActivationExpired,
      });
    }
  },
  async actionRegenerateActivationMail(
    context: MainContext,
    payload: { email: string }
  ) {
    const notificationActivating = {
      messageKey: m.accountActivationMailSent,
      showProgress: true,
    };
    const notificationActivated = {
      messageKey: m.accountActivationReceived,
      showProgress: false,
    };
    try {
      commitAddNotification(context, notificationActivating);
      await generalApi.regenerateActivationMail({ email: payload.email });
      commitRemoveNotification(context, notificationActivating);
      commitAddNotification(context, notificationActivated);
    } catch (error) {
      commitRemoveNotification(context, notificationActivating);
      commitAddNotification(context, {
        color: 'error',
        messageKey: m.processingError,
      });
    }
  },
  async actionGetSendableCompanyUsers(
    context: MainContext,
    payload: {
      companyId: string;
    }
  ) {
    try {
      const response = await generalApi.getSendableCompanyUsers(
        payload.companyId
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  async actionGetCompanyPeriodicExportSettings(
    context: MainContext,
    payload: {
      companyId: string;
    }
  ) {
    try {
      const response = await generalApi.getCompanyPeriodicExportSettings(
        payload.companyId
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  async actionGetCompanyPeriodicExportSettingDetail(
    context: MainContext,
    payload: {
      companyId: string;
      settingId: number;
    }
  ) {
    try {
      const response = await generalApi.getCompanyPeriodicExportSettingDetail(
        payload.companyId,
        payload.settingId
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  async actionCreateCompanyPeriodicExportSetting(
    context: MainContext,
    payload: {
      companyId: string;
      body: CreateCompanyPeriodicExportSettingRequest;
    }
  ) {
    try {
      const response = await generalApi.createCompanyPeriodicExportSetting(
        payload.companyId,
        payload.body
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  async actionUpdateCompanyPeriodicExportSetting(
    context: MainContext,
    payload: {
      companyId: string;
      settingId: number;
      body: UpdateCompanyPeriodicExportSettingRequest;
    }
  ) {
    try {
      const response = await generalApi.updateCompanyPeriodicExportSetting(
        payload.companyId,
        payload.settingId,
        payload.body
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  async actionDeleteCompanyPeriodicExportSetting(
    context: MainContext,
    payload: {
      companyId: string;
      settingId: number;
    }
  ) {
    try {
      const response = await generalApi.deleteCompanyPeriodicExportSetting(
        payload.companyId,
        payload.settingId
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  async actionGetCompanySds(
    context: MainContext,
    payload: {
      companyId: string;
      sdsId: string;
    }
  ) {
    try {
      const response = await generalApi.getCompanySds(
        payload.companyId,
        payload.sdsId
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionGetCompanySdses(
    context: MainContext,
    payload: {
      companyId: string;
      data: GetCompanySdsesRequestQuery;
      signal?: AbortSignal;
    }
  ) {
    try {
      const response = await generalApi.getCompanySdses(
        payload.companyId,
        payload.data,
        payload.signal
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  // エクスポートモーダルで実行可能な操作情報を取得する
  async actionGetCompanyExportOperations(
    context: MainContext,
    payload: {
      companyId: string;
      language?: string;
    }
  ) {
    try {
      const response = await generalApi.getCompanyExportOperations(
        payload.companyId,
        payload.language
      );

      return response;
    } catch (error) {
      // TODO: Add proper error handling
      const axiosError = error as AxiosError<ApiErrorResponse>;
      throw axiosError;
    }
  },

  async actionExportCompanySdses(
    context: MainContext,
    payload: {
      companyId: string;
      data: ExportCompanySdsesRequest;
    }
  ) {
    try {
      const response = await generalApi.exportCompanySdses(
        payload.companyId,
        payload.data
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.status !== 400) {
        await dispatchCheckApiError(context, axiosError);
      } else {
        const { detail, denied_sds_list: deniedSdsList } = axiosError.response
          .data as ApiErrorResponse;
        if (detail && deniedSdsList) {
          commitAddNotification(context, {
            messageKey: m.pleaseCheck,
            messageValues: {
              detail: detail ?? '',
              issues: (deniedSdsList ?? []).map((s) => s.name).join(', '),
            },
            color: 'error',
          });
        } else if (detail) {
          console.log('detail', detail);
          commitAddNotification(context, {
            messageKey: m.errorUpdatingData,
            messageValues: { details: detail },
            color: 'error',
          });
        } else {
          const errorData = (error as AxiosError).response?.data;
          const errorMessage =
            typeof errorData === 'string'
              ? errorData
              : (error as AxiosError).message;
          commitAddNotification(context, {
            messageKey: m.unknownError,
            messageValues: { details: errorMessage },
            color: 'error',
          });
        }
      }
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionRiskAssessmentExport(
    context: MainContext,
    payload: {
      companyId: string;
      data: RiskAssessmentExportRequest;
    }
  ) {
    try {
      const response = await generalApi.riskAssessmentExport(
        payload.companyId,
        payload.data
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      throw axiosError;
    }
  },

  async actionGetCompanySdsesCount(
    context: MainContext,
    payload: {
      companyId: string;
      data: GetCompanySdsesCountRequestQuery;
      signal?: AbortSignal;
    }
  ) {
    try {
      const response = await generalApi.getCompanySdsesCount(
        payload.companyId,
        payload.data,
        payload.signal
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionGetCompanySdsPdf(
    context: MainContext,
    payload: {
      companyId: string;
      fileUuid: string;
    }
  ) {
    try {
      const response = await generalApi.getCompanySdsPdf(
        payload.companyId,
        payload.fileUuid
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionGetCompanySdsAttachmentPdf(
    context: MainContext,
    payload: {
      companyId: string;
      sdsUuid: string;
      attachmentUuid: string;
    }
  ) {
    try {
      const response = await generalApi.getCompanySdsAttachmentPdf(
        payload.companyId,
        payload.sdsUuid,
        payload.attachmentUuid
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionGetCompanySdsJson(
    context: MainContext,
    payload: {
      companyId: string;
      sdsUuid: string;
    }
  ) {
    try {
      const response = await generalApi.getCompanySdsJson(
        payload.companyId,
        payload.sdsUuid
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionExportCompanySdsJsonToPdf(
    context: MainContext,
    payload: {
      companyId: string;
      data: FormData;
    }
  ) {
    try {
      const response = await generalApi.exportCompanySdsJsonToPdf(
        payload.companyId,
        payload.data
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionGetGcsSignedUrl(
    context: MainContext,
    payload: {
      companyId: string;
      sdsList: Array<CreateCompanySdsRequestUnit>;
    }
  ) {
    try {
      const response = await generalApi.getGcsSignedUrl(
        payload.companyId,
        payload.sdsList
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionGetAttachmentGcsSignedUrl(
    context: MainContext,
    payload: {
      companyId: string;
      sdsId: string;
      attachments: Array<CreateAttachmentRequestUnit>;
    }
  ) {
    try {
      const response = await generalApi.getAttachmentGcsSignedUrl(
        payload.companyId,
        payload.sdsId,
        payload.attachments
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionCreateCompanySdses(
    context: MainContext,
    payload: {
      companyId: string;
      departmentIdList: string[];
      sdsList: Array<CreateCompanySdsRequestUnit>;
    }
  ) {
    try {
      const { companyId, departmentIdList, sdsList } = payload;
      const response = await generalApi.createSdses(companyId, {
        department_id_list: departmentIdList,
        sds_list: sdsList,
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      commitAddNotification(context, {
        messageKey: m.uploadFailed,
        color: 'error',
      });
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionCheckCompanySdsesDuplicates(
    context: MainContext,
    payload: {
      companyId: string;
      sdsList: Array<CheckDuplicateSdsesRequestUnit>;
    }
  ) {
    try {
      const { companyId, sdsList } = payload;
      const response = await generalApi.checkSdsesDuplicates(companyId, {
        sds_id_list: sdsList,
      });
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionUpdateCompanySdses(
    context: MainContext,
    payload: {
      companyId: string;
      data: UpdateCompanySdsesRequestBody;
    }
  ) {
    try {
      const { companyId, data } = payload;
      const response = await generalApi.updateCompanySdses(companyId, data);

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionDeleteCompanySdses(
    context: MainContext,
    payload: {
      companyId: string;
      data: DeleteCompanySdsesRequestBody;
    }
  ) {
    try {
      const { companyId, data } = payload;
      const response = await generalApi.deleteCompanySdses(companyId, data);
      commitAddNotification(context, {
        messageKey: m.fileDeleted,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      if (axiosError.response?.status !== 400) {
        await dispatchCheckApiError(context, axiosError);
      } else {
        const { detail, denied_sds_list: deniedSdsList } = axiosError.response
          .data as ApiErrorResponse;
        if (detail) {
          commitAddNotification(context, {
            messageKey: m.pleaseCheck,
            messageValues: {
              detail: detail ?? '',
              issues: (deniedSdsList ?? []) // Fallback to an empty array
                .map((s) => s.name)
                .join(', '),
            },
            color: 'error',
          });
        } else {
          const errorData = (error as AxiosError).response?.data;
          const errorMessage =
            typeof errorData === 'string'
              ? errorData
              : (error as AxiosError).message;
          commitAddNotification(context, {
            messageKey: m.unknownError,
            messageValues: { details: errorMessage },
            color: 'error',
          });
        }
      }
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionPdfDownloadCount(
    context: MainContext,
    payload: {
      companyId: string;
      signal?: AbortSignal;
    }
  ) {
    try {
      const { companyId, signal } = payload;
      const response = await generalApi.downloadPdfCount(companyId, signal);
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionDownloadCompanySdsesPDFBulk(
    context: MainContext,
    payload: {
      companyId: string;
      data: DownloadCompanySdsesPDFRequestQuery;
    }
  ) {
    try {
      const { companyId, data } = payload;
      const response = await generalApi.downloadCompanySdsesPDFBulk(
        companyId,
        data
      );
      commitAddNotification(context, {
        messageKey: m.pdfDownloadSuccess,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionDownloadCompanySdsesCSV(
    context: MainContext,
    payload: {
      companyId: string;
      data: DownloadCompanySdsesCSVRequestQuery;
    }
  ) {
    try {
      const { companyId, data } = payload;
      const response = await generalApi.downloadCompanySdsesCSV(
        companyId,
        data
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionDownloadRiskAssessmentRecomputeCSV(
    context: MainContext,
    payload: {
      companyId: string;
      data: DownloadRiskAssessmentRecomputeCSVRequestQuery;
    }
  ) {
    try {
      const { companyId, data } = payload;
      const response = await generalApi.downloadRiskAssessmentRecomputeCSV(
        companyId,
        data
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async dispatchUploadCompanySdsesCSV(
    context: MainContext,
    payload: {
      companyId: string;
      data: UploadCompanySdsesCSVRequestBody;
    }
  ) {
    try {
      const { companyId, data } = payload;
      const response = await generalApi.uploadCompanySdsesCSV(companyId, data);

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async dispatchUploadRiskAssessmentRecomputeCSV(
    context: MainContext,
    payload: {
      companyId: string;
      data: FormData;
    }
  ) {
    try {
      const { companyId, data } = payload;
      return await generalApi.uploadRiskAssessmentRecomputeCSV(companyId, data);
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      // 400(CSVバリデーションエラー)は専用ダイアログで表示するため汎用トーストは出さない
      if (axiosError.response?.status !== 400) {
        await dispatchCheckApiError(context, axiosError);
      }
      throw axiosError;
    }
  },

  async actionUpdateCompanySds(
    context: MainContext,
    payload: {
      companyId: string;
      fileId: string;
      form: UpdateCompanySdsRequestBody;
    }
  ) {
    try {
      const response = await generalApi.updateCompanySds(
        payload.companyId,
        payload.fileId,
        payload.form
      );
      commitAddNotification(context, {
        messageKey: m.updateSuccess,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (axiosError?.response?.status === 409) {
        commitAddNotification(context, {
          messageKey: m.productCodeAlreadyExists,
          color: 'error',
        });
      } else {
        commitAddNotification(context, {
          messageKey: m.processFailed,
          color: 'error',
        });
      }
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  actionGetDynamicList,
  actionUpdateDynamicList,
  actionGetDynamicLists,
  actionGetDynamicListResults,
  actionGetExportDynamicListResults,
  actionCreateDynamicList,
  actionRequestDynamicList,
  actionResumeDynamicLists,
  actionSuspendDynamicLists,
  actionDeleteDynamicLists,
  actionFavoriteDynamicLists,
  actionUnfavoriteDynamicLists,
  actionGetDynamicListTemplates,
  actionGetDynamicListTemplate,
  actionGetDynamicListCategories,
  actionCreateDynamicListFromTemplate,

  async actionUpdateReconfirmationSds(
    context: MainContext,
    payload: {
      companyId: string;
      sdsId: string;
      form: UpdateReconfirmationSdsRequestBody;
    }
  ) {
    try {
      const response = await generalApi.updateReconfirmationSds(
        payload.companyId,
        payload.sdsId,
        payload.form
      );
      commitAddNotification(context, {
        messageKey: m.updateSuccess,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      commitAddNotification(context, {
        messageKey: m.processFailed,
        color: 'error',
      });
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionGetExportCreateSimple(
    context: MainContext,
    payload: {
      sdsId: string;
    }
  ) {
    try {
      const response = await generalApi.getExportCreateSimple(payload.sdsId);
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionGetExportCreateSimpleV3(
    context: MainContext,
    payload: {
      sdsId: string;
    }
  ) {
    try {
      const response = await generalApi.getExportCreateSimpleV3(payload.sdsId);
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionGetExportCreateSimpleV3_2(
    context: MainContext,
    payload: {
      sdsId: string;
    }
  ) {
    try {
      const response = await generalApi.getExportCreateSimpleV3_2(
        payload.sdsId
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionCancelExportCreateSimple(context: MainContext) {
    try {
      const response = generalApi.cancelCreateSimpleExportRequest();
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
    }
  },

  async actionCancelExportCreateSimpleV3(context: MainContext) {
    try {
      const response = generalApi.cancelCreateSimpleV3ExportRequest();
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
    }
  },

  async actionCancelExportCreateSimpleV3_2(context: MainContext) {
    try {
      const response = generalApi.cancelCreateSimpleV3_2ExportRequest();
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
    }
  },

  async actionCreateReanalysisSds(
    context: MainContext,
    payload: {
      sdsId: string;
      comment: string;
      requestType: string;
    }
  ) {
    try {
      const response = await generalApi.createReanalysisSds(payload);

      commitAddNotification(context, {
        messageKey: m.redataRequested,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
    }
  },

  async actionPutDynamicListResult(
    context: MainContext,
    payload: {
      sdsId: string;
      dynamicListId: number;
      dynamicListResult: DynamicListResultRequestBody;
    }
  ) {
    try {
      const response = await generalApi.putDynamicListResult(
        payload.sdsId,
        payload.dynamicListId,
        payload.dynamicListResult
      );
      commitAddNotification(context, {
        messageKey: m.checklistUpdated,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      commitAddNotification(context, {
        messageKey: m.processFailed,
        color: 'error',
      });
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionPostDynamicListResult(
    context: MainContext,
    payload: {
      sdsId: string;
      dynamicListId: number;
    }
  ) {
    try {
      const response = await generalApi.postDynamicListResult(
        payload.sdsId,
        payload.dynamicListId
      );
      commitAddNotification(context, {
        messageKey: m.checklistAdded,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      commitAddNotification(context, {
        messageKey: m.processFailed,
        color: 'error',
      });
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionDeleteDynamicListResult(
    context: MainContext,
    payload: {
      sdsId: string;
      dynamicListId: number;
    }
  ) {
    try {
      const response = await generalApi.deleteDynamicListResult(
        payload.sdsId,
        payload.dynamicListId
      );
      commitAddNotification(context, {
        messageKey: m.checklistRemoved,
        color: 'success',
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      commitAddNotification(context, {
        messageKey: m.processFailed,
        color: 'error',
      });
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  async actionSetUserLocale(context: MainContext, locale: Locale) {
    const user = context.state.userProfile;
    if (user) {
      try {
        // Update language in database
        await generalApi.updateUserLanguage({
          locale: locale,
        });
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>;
        await dispatchCheckApiError(context, axiosError);
        commitAddNotification(context, {
          messageKey: m.processFailed,
          color: 'error',
        });
        if (shouldLogOutOnForbidden(axiosError)) {
          await dispatchLogOut(context);
        }
        throw axiosError;
      }
    }
    commitSetUserLocale(context, locale);
  },

  actionGetCompanyTags,
  actionCreateCompanyTag,
  actionBulkCreateCompanyTag,
  actionUpdateCompanyTag,
  actionDeleteCompanyTag,
  actionGetEditHistories,
  actionGetCompanySAMLSetting,
  actionGetCompanyPasswordSetting,
  actionUpdateCompanyPasswordSetting,
  actionGetEnforce2Fa,
  actionUpdateEnforce2Fa,
  actionFetchForceEnroll2Fa,
  actionUpdateFeatureFlags,
  actionStartPollingOfFeatureFlags,

  async actionGetMSdsSection2Categories(context: MainContext) {
    try {
      const response = await generalApi.getMSdsSection2Categories();
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionGetMSdsSection2Items(context: MainContext) {
    try {
      const response = await generalApi.getMSdsSection2Items();
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
  async actionGetMSdsSection2Hazards(context: MainContext) {
    try {
      const response = await generalApi.getMSdsSection2Hazards();
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },

  // 切り替え可能テナントの取得
  async actionGetSwitchableCompanies(context: MainContext) {
    try {
      const response = await generalApi.getSwitchableCompanies();
      if (response.data) {
        // Map API response to Company interface format
        const companies = response.data.map((company) => ({
          name: company.name,
          uuid: company.uuid,
          phone: company.phone,
          country_code: company.country_code,
          maximum_upload: company.maximum_upload,
          attachment_limit_id: company.attachment_limit_id,
          uploaded_file_number: 0, // Not provided by this endpoint
          customer_id: company.customer_id,
          subscription_id: company.subscription_id,
          next_paid_at: company.next_paid_at
            ? new Date(company.next_paid_at)
            : null,
          renewed_at: company.renewed_at ? new Date(company.renewed_at) : null,
          subscribed_at: company.subscribed_at
            ? new Date(company.subscribed_at)
            : null,
          active_features: company.active_features || [],
          sds_expiration_years: company.sds_expiration_years || 0,
          sds_revision_copy_fields: company.sds_revision_copy_fields || {},
          is_switchable: true, // This endpoint only returns switchable companies
        }));
        commitSetSwitchableCompanies(context, companies);
      }
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      await dispatchCheckApiError(context, axiosError);
      if (shouldLogOutOnForbidden(axiosError)) {
        await dispatchLogOut(context);
      }
      throw axiosError;
    }
  },
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { dispatch } = getStoreAccessors<MainState | any, State>('');

export const dispatchInit = dispatch(actionsMain.actionInit);
export const dispatchPostInquiry = dispatch(actionsMain.actionPostInquiry);
export const dispatchPostInquiryEn = dispatch(actionsMain.actionPostInquiryEn);
export const dispatchPostQuotation = dispatch(actionsMain.actionPostQuoation);
export const dispatchCheckApiError = dispatch(actionsMain.actionCheckApiError);
export const dispatchGetCompany = dispatch(actionsMain.actionGetCompany);
export const dispatchGetCompanyDepartments = dispatch(
  actionsMain.actionGetCompanyDepartments
);
export const dispatchGetCompanyDepartmentTree = dispatch(
  actionsMain.actionGetCompanyDepartmentTree
);
export const dispatchGetCompanyDepartmentDeletionImpact = dispatch(
  actionsMain.actionGetCompanyDepartmentDeletionImpact
);
export const dispatchCreateCompanyDepartment = dispatch(
  actionsMain.actionCreateCompanyDepartment
);
export const dispatchUpdateCompanyDepartment = dispatch(
  actionsMain.actionUpdateCompanyDepartment
);
export const dispatchMoveCompanyDepartment = dispatch(
  actionsMain.actionMoveCompanyDepartment
);
export const dispatchDeleteCompanyDepartment = dispatch(
  actionsMain.actionDeleteCompanyDepartment
);
export const dispatchGetCompanyDepartmentsCsv = dispatch(
  actionsMain.actionGetCompanyDepartmentsCsv
);
export const dispatchPostCompanyDepartmentsCsv = dispatch(
  actionsMain.actionPostCompanyDepartmentsCsv
);
export const dispatchUpdateCompany = dispatch(actionsMain.actionUpdateCompany);
export const dispatchUpdateCompanySettings = dispatch(
  actionsMain.actionUpdateCompanySettings
);
export const dispatchDeleteCompany = dispatch(actionsMain.actionDeleteCompany);
export const dispatchGetCurrentUser = dispatch(
  actionsMain.actionGetCurrentUser
);
export const dispatchSignUp = dispatch(actionsMain.actionSignUp);
export const dispatchLogIn = dispatch(actionsMain.actionLogIn);
export const dispatchLogOut = dispatch(actionsMain.actionLogOut);
export const dispatchRegenerateActivationMail = dispatch(
  actionsMain.actionRegenerateActivationMail
);
export const dispatchResetCompanyUserPassword = dispatch(
  actionsMain.actionResetCompanyUserPassword
);
export const dispatchRouteLoggedIn = dispatch(actionsMain.actionRouteLoggedIn);
export const dispatchUpdateCurrentUser = dispatch(
  actionsMain.actionUpdateCurrentUser
);
export const dispatchUpdateCompanyUser = dispatch(
  actionsMain.actionUpdateCompanyUser
);
export const dispatchRemoveNotification = dispatch(
  actionsMain.removeNotification
);
export const dispatchPasswordRecovery = dispatch(actionsMain.passwordRecovery);
export const dispatchResetPassword = dispatch(actionsMain.resetPassword);
export const dispatchActivateUser = dispatch(actionsMain.actionActivateUser);
export const dispatchGetCompanyUsers = dispatch(
  actionsMain.actionGetCompanyUsers
);
export const dispatchCreateCompanyUser = dispatch(
  actionsMain.actionCreateCompanyUser
);
export const dispatchDeleteCompanyUser = dispatch(
  actionsMain.actionDeleteCompanyUser
);
export const dispatchGetCompanyUserCsv = dispatch(
  actionsMain.actionGetCompanyUserCsv
);
export const dispatchPostCompanyUserCsv = dispatch(
  actionsMain.actionPostCompanyUserCsv
);
export const dispatchGetCompanySds = dispatch(actionsMain.actionGetCompanySds);
export const dispatchGetCompanySdses = dispatch(
  actionsMain.actionGetCompanySdses
);
export const dispatchGetCompanyExportOperations = dispatch(
  actionsMain.actionGetCompanyExportOperations
);
export const dispatchExportSdses = dispatch(
  actionsMain.actionExportCompanySdses
);
export const dispatchRiskAssessmentExport = dispatch(
  actionsMain.actionRiskAssessmentExport
);
export const dispatchGetCompanySdsesCount = dispatch(
  actionsMain.actionGetCompanySdsesCount
);
export const dispatchGetCompanySdsPdf = dispatch(
  actionsMain.actionGetCompanySdsPdf
);
export const dispatchGetCompanySdsAttachmentPdf = dispatch(
  actionsMain.actionGetCompanySdsAttachmentPdf
);
export const dispatchGetCompanySdsJson = dispatch(
  actionsMain.actionGetCompanySdsJson
);
export const dispatchCreateCompanySdses = dispatch(
  actionsMain.actionCreateCompanySdses
);
export const dispatchCheckCompanySdsesDuplicates = dispatch(
  actionsMain.actionCheckCompanySdsesDuplicates
);
export const dispatchDeleteCompanySdses = dispatch(
  actionsMain.actionDeleteCompanySdses
);

export const dispatchUpdateCompanySds = dispatch(
  actionsMain.actionUpdateCompanySds
);
export const dispatchUpdateCompanySdses = dispatch(
  actionsMain.actionUpdateCompanySdses
);
export const dispatchUploadCompanySdsesCSV = dispatch(
  actionsMain.dispatchUploadCompanySdsesCSV
);
export const dispatchDownloadCompanySdsesCSV = dispatch(
  actionsMain.actionDownloadCompanySdsesCSV
);
export const dispatchDownloadRiskAssessmentRecomputeCSV = dispatch(
  actionsMain.actionDownloadRiskAssessmentRecomputeCSV
);
export const dispatchUploadRiskAssessmentRecomputeCSV = dispatch(
  actionsMain.dispatchUploadRiskAssessmentRecomputeCSV
);
export const dispatchGetPdfDownloadCount = dispatch(
  actionsMain.actionPdfDownloadCount
);
export const dispatchDownloadCompanySdsesPDFBulk = dispatch(
  actionsMain.actionDownloadCompanySdsesPDFBulk
);
export const dispatchUpdateReconfirmationSds = dispatch(
  actionsMain.actionUpdateReconfirmationSds
);
export const dispatchGetCompanyWhitelistIp = dispatch(
  actionsMain.actionGetCompanyWhitelistIp
);
export const dispatchCreateCompanyWhitelistIp = dispatch(
  actionsMain.actionCreateCompanyWhitelistIp
);
export const dispatchCreateCompanyWhitelistIpBulk = dispatch(
  actionsMain.actionCreateCompanyWhitelistIpBulk
);
export const dispatchDeleteCompanyWhitelistIp = dispatch(
  actionsMain.actionDeleteCompanyWhitelistIp
);
export const dispatchUpdateCompanyWhitelistIpName = dispatch(
  actionsMain.actionUpdateCompanyWhitelistIpName
);
export const dispatchGetSendableCompanyUsers = dispatch(
  actionsMain.actionGetSendableCompanyUsers
);
export const dispatchGetCompanyPeriodicExportSettings = dispatch(
  actionsMain.actionGetCompanyPeriodicExportSettings
);
export const dispatchGetCompanyPeriodicExportSettingDetail = dispatch(
  actionsMain.actionGetCompanyPeriodicExportSettingDetail
);
export const dispatchCreateCompanyPeriodicExportSetting = dispatch(
  actionsMain.actionCreateCompanyPeriodicExportSetting
);
export const dispatchUpdateCompanyPeriodicExportSetting = dispatch(
  actionsMain.actionUpdateCompanyPeriodicExportSetting
);
export const dispatchDeleteCompanyPeriodicExportSetting = dispatch(
  actionsMain.actionDeleteCompanyPeriodicExportSetting
);
export const dispatchGetGcsSignedUrl = dispatch(
  actionsMain.actionGetGcsSignedUrl
);
export const dispatchExportCompanySdsJsonToPdf = dispatch(
  actionsMain.actionExportCompanySdsJsonToPdf
);
export const dispatchGetAttachmentGcsSignedUrl = dispatch(
  actionsMain.actionGetAttachmentGcsSignedUrl
);
export const dispatchGetClientIp = dispatch(actionsMain.actionGetClientIp);
export const dispatchGetDynamicList = dispatch(
  actionsMain.actionGetDynamicList
);
export const dispatchUpdateDynamicList = dispatch(
  actionsMain.actionUpdateDynamicList
);
export const dispatchGetDynamicLists = dispatch(
  actionsMain.actionGetDynamicLists
);
export const dispatchResumeDynamicLists = dispatch(
  actionsMain.actionResumeDynamicLists
);
export const dispatchSuspendDynamicLists = dispatch(
  actionsMain.actionSuspendDynamicLists
);
export const dispatchDeleteDynamicLists = dispatch(
  actionsMain.actionDeleteDynamicLists
);
export const dispatchFavoriteDynamicLists = dispatch(
  actionsMain.actionFavoriteDynamicLists
);
export const dispatchUnfavoriteDynamicLists = dispatch(
  actionsMain.actionUnfavoriteDynamicLists
);
export const dispatchGetDynamicListTemplates = dispatch(
  actionsMain.actionGetDynamicListTemplates
);
export const dispatchGetDynamicListTemplate = dispatch(
  actionsMain.actionGetDynamicListTemplate
);
export const dispatchGetDynamicListCategories = dispatch(
  actionsMain.actionGetDynamicListCategories
);
export const dispatchCreateDynamicListFromTemplate = dispatch(
  actionsMain.actionCreateDynamicListFromTemplate
);
export const dispatchGetDynamicListResults = dispatch(
  actionsMain.actionGetDynamicListResults
);
export const dispatchGetExportDynamicListResults = dispatch(
  actionsMain.actionGetExportDynamicListResults
);
export const dispatchCreateDynamicList = dispatch(
  actionsMain.actionCreateDynamicList
);
export const dispatchRequestDynamicList = dispatch(
  actionsMain.actionRequestDynamicList
);
export const dispatchGetExportCreateSimple = dispatch(
  actionsMain.actionGetExportCreateSimple
);
export const dispatchGetExportCreateSimpleV3 = dispatch(
  actionsMain.actionGetExportCreateSimpleV3
);
export const dispatchGetExportCreateSimpleV3_2 = dispatch(
  actionsMain.actionGetExportCreateSimpleV3_2
);
export const dispatchCancelCreateSimpleExportRequest = dispatch(
  actionsMain.actionCancelExportCreateSimple
);
export const dispatchCancelCreateSimpleV3ExportRequest = dispatch(
  actionsMain.actionCancelExportCreateSimpleV3
);
export const dispatchCancelCreateSimpleV3_2ExportRequest = dispatch(
  actionsMain.actionCancelExportCreateSimpleV3_2
);
export const dispatchCreateReanalysisSds = dispatch(
  actionsMain.actionCreateReanalysisSds
);
export const dispatchPutDynamicListResult = dispatch(
  actionsMain.actionPutDynamicListResult
);
export const dispatchPostDynamicListResult = dispatch(
  actionsMain.actionPostDynamicListResult
);
export const dispatchDeleteDynamicListResult = dispatch(
  actionsMain.actionDeleteDynamicListResult
);
export const dispatchSetUserLocale = dispatch(actionsMain.actionSetUserLocale);

export const dispatchCreateCompanyTag = dispatch(
  actionsMain.actionCreateCompanyTag
);

export const dispatchBulkCreateCompanyTag = dispatch(
  actionsMain.actionBulkCreateCompanyTag
);

export const dispatchGetCompanyTags = dispatch(
  actionsMain.actionGetCompanyTags
);

export const dispatchDeleteCompanyTag = dispatch(
  actionsMain.actionDeleteCompanyTag
);

export const dispatchUpdateCompanyTag = dispatch(
  actionsMain.actionUpdateCompanyTag
);

export const dispatchGetUserSearchCriteria = dispatch(
  actionsMain.actionGetUserSearchCriteria
);
export const dispatchCreateUserSearchCriteria = dispatch(
  actionsMain.actionCreateUserSearchCriteria
);
export const dispatchUpdateUserSearchCriteria = dispatch(
  actionsMain.actionUpdateUserSearchCriteria
);
export const dispatchDeleteUserSearchCriteria = dispatch(
  actionsMain.actionDeleteUserSearchCriteria
);

export const dispatchGetEditHistories = dispatch(
  actionsMain.actionGetEditHistories
);

export const dispatchGetCompanySAMLSetting = dispatch(
  actionsMain.actionGetCompanySAMLSetting
);

export const dispatchGetCompanyPasswordSetting = dispatch(
  actionsMain.actionGetCompanyPasswordSetting
);

export const dispatchUpdateCompanyPasswordSetting = dispatch(
  actionsMain.actionUpdateCompanyPasswordSetting
);

export const dispatchGetEnforce2Fa = dispatch(actionsMain.actionGetEnforce2Fa);

export const dispatchUpdateEnforce2Fa = dispatch(
  actionsMain.actionUpdateEnforce2Fa
);

export const dispatchFetchForceEnroll2Fa = dispatch(
  actionsMain.actionFetchForceEnroll2Fa
);

export const dispatchUpdateFeatureFlags = dispatch(
  actionsMain.actionUpdateFeatureFlags
);

export const dispatchStartPollingOfFeatureFlags = dispatch(
  actionsMain.actionStartPollingOfFeatureFlags
);

export const dispatchGetMSdsSection2Categories = dispatch(
  actionsMain.actionGetMSdsSection2Categories
);
export const dispatchGetMSdsSection2Items = dispatch(
  actionsMain.actionGetMSdsSection2Items
);
export const dispatchGetMSdsSection2Hazards = dispatch(
  actionsMain.actionGetMSdsSection2Hazards
);
export const dispatchGetSwitchableCompanies = dispatch(
  actionsMain.actionGetSwitchableCompanies
);
